import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  RefreshCcw,
  Download,
  Send,
  Eye,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Filter,
  Search,
  ChevronRight,
  Mail,
  FileCheck,
  FilePlus,
  Printer
} from 'lucide-react';
import { cn } from '../../lib/utils';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

interface Statement {
  id: number;
  statementNumber: string;
  householdId: number;
  householdName: string;
  portfolioId: number;
  portfolioName: string;
  statementType: 'ACCOUNT_STATEMENT' | 'QUARTERLY_REVIEW' | 'ANNUAL_REVIEW' | 'PERFORMANCE_REPORT';
  periodType: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'CUSTOM';
  periodStart: string;
  periodEnd: string;
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'PUBLISHED' | 'DELIVERED';
  deliveryMethod: 'EMAIL' | 'MAIL' | 'BOTH';
  generatedAt: string;
  sentAt: string | null;
  viewCount: number;
  beginningValue: number;
  endingValue: number;
  periodReturn: number;
}

const ClientStatementsScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [statements, setStatements] = useState<Statement[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'delivered'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchStatements();
  }, [selectedPeriod]);

  const fetchStatements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/statements?period=${selectedPeriod}`);
      setStatements(response.data?.content || response.data || []);
    } catch (error) {
      console.error('Failed to fetch statements:', error);
      // No mock data - show empty state
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStatements = async () => {
    setGenerating(true);
    try {
      await axios.post('/api/statements/generate-batch', { period: 'MONTHLY' });
      await fetchStatements();
    } catch (error) {
      console.error('Failed to generate statements:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await axios.post(`/api/statements/${id}/approve`);
      setStatements(prev => prev.map(s => s.id === id ? { ...s, status: 'APPROVED' as const } : s));
    } catch (error) {
      console.error('Failed to approve statement:', error);
    }
  };

  const handleSend = async (id: number) => {
    try {
      await axios.post(`/api/statements/${id}/send`);
      setStatements(prev => prev.map(s => s.id === id ? { ...s, status: 'DELIVERED' as const, sentAt: new Date().toISOString() } : s));
    } catch (error) {
      console.error('Failed to send statement:', error);
    }
  };

  const handleDownload = async (statement: Statement) => {
    try {
      const response = await axios.get(`/api/statements/${statement.id}/download`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${statement.statementNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download statement:', error);
      // Show user-friendly error
      alert('Unable to download statement. The PDF may still be generating.');
    }
  };

  const handlePreview = async (statement: Statement) => {
    try {
      const response = await axios.get(`/api/statements/${statement.id}/download`, {
        responseType: 'blob'
      });

      // Open in new tab for preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to preview statement:', error);
      alert('Unable to preview statement. The PDF may still be generating.');
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);
  };

  const getStatusBadge = (status: Statement['status']) => {
    const styles = {
      DRAFT: 'bg-slate-800 text-slate-400 border-slate-700',
      PENDING_APPROVAL: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      APPROVED: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      PUBLISHED: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
      DELIVERED: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    };
    const labels = {
      DRAFT: 'Draft',
      PENDING_APPROVAL: 'Pending',
      APPROVED: 'Ready',
      PUBLISHED: 'Published',
      DELIVERED: 'Sent'
    };
    return (
      <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border", styles[status])}>
        {labels[status]}
      </span>
    );
  };

  const getTypeLabel = (type: Statement['statementType']) => {
    const labels = {
      ACCOUNT_STATEMENT: 'Account Statement',
      QUARTERLY_REVIEW: 'Quarterly Review',
      ANNUAL_REVIEW: 'Annual Review',
      PERFORMANCE_REPORT: 'Performance Report'
    };
    return labels[type];
  };

  const filteredStatements = statements.filter(s => {
    if (activeTab === 'pending' && !['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(s.status)) return false;
    if (activeTab === 'delivered' && s.status !== 'DELIVERED') return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return s.householdName.toLowerCase().includes(term) ||
             s.portfolioName.toLowerCase().includes(term) ||
             s.statementNumber.toLowerCase().includes(term);
    }
    return true;
  });

  const pendingCount = statements.filter(s => ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'].includes(s.status)).length;
  const deliveredCount = statements.filter(s => s.status === 'DELIVERED').length;

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-end"
        >
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <FileText className="w-8 h-8 text-violet-500" />
              Client Statements
              <span className="ml-2 px-3 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-xs font-bold text-violet-400 uppercase tracking-widest">
                RIA
              </span>
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Generate, approve, and deliver client account statements and performance reports
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleGenerateStatements}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-50"
            >
              <FilePlus className={cn("w-4 h-4", generating && "animate-pulse")} />
              Generate Monthly
            </button>
            <button
              onClick={fetchStatements}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
            >
              <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
              Refresh
            </button>
          </div>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <GlassCard className="p-6 border-l-4 border-l-violet-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Statements</div>
                <div className="text-2xl font-bold text-white tracking-tight">{statements.length}</div>
                <div className="text-[10px] text-slate-500 mt-2">This period</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400">
                <FileText className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-amber-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Pending Action</div>
                <div className="text-2xl font-bold text-amber-400 tracking-tight">{pendingCount}</div>
                <div className="text-[10px] text-slate-500 mt-2">Awaiting approval/send</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400">
                <Clock className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-emerald-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Delivered</div>
                <div className="text-2xl font-bold text-emerald-400 tracking-tight">{deliveredCount}</div>
                <div className="text-[10px] text-slate-500 mt-2">Sent to clients</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Client Views</div>
                <div className="text-2xl font-bold text-blue-400 tracking-tight">
                  {statements.reduce((sum, s) => sum + s.viewCount, 0)}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Portal downloads</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                <Eye className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Filters & Search */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
              {[
                { id: 'all', label: 'All', count: statements.length },
                { id: 'pending', label: 'Pending', count: pendingCount },
                { id: 'delivered', label: 'Delivered', count: deliveredCount }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={cn(
                    "px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2",
                    activeTab === tab.id ? "bg-violet-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                  )}
                >
                  {tab.label}
                  <span className={cn(
                    "px-1.5 py-0.5 rounded-md text-[10px]",
                    activeTab === tab.id ? "bg-white/20" : "bg-slate-800"
                  )}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search household, account..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-500/50 transition-all w-64"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm text-white focus:outline-none cursor-pointer"
            >
              <option value="current">Current Month</option>
              <option value="previous">Previous Month</option>
              <option value="q4-2023">Q4 2023</option>
              <option value="2023">Full Year 2023</option>
            </select>
          </div>
        </div>

        {/* Statements Table */}
        <GlassCard>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/30">
                  <th className="py-4 px-4">Statement</th>
                  <th className="py-4 px-4">Household</th>
                  <th className="py-4 px-4">Account</th>
                  <th className="py-4 px-4">Type</th>
                  <th className="py-4 px-4">Period</th>
                  <th className="py-4 px-4 text-right">Value Change</th>
                  <th className="py-4 px-4 text-right">Return</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-center">Views</th>
                  <th className="py-4 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredStatements.map((statement) => (
                  <tr key={statement.id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                    <td className="py-4 px-4">
                      <div className="font-mono text-xs text-blue-400">{statement.statementNumber}</div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {new Date(statement.generatedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-4 px-4 font-bold text-white">{statement.householdName}</td>
                    <td className="py-4 px-4 text-slate-400">{statement.portfolioName}</td>
                    <td className="py-4 px-4">
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-800 text-slate-400 border border-white/5">
                        {getTypeLabel(statement.statementType)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-slate-400 text-xs">
                      {new Date(statement.periodStart).toLocaleDateString()} - {new Date(statement.periodEnd).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="font-mono text-slate-400 text-xs">{formatCurrency(statement.beginningValue)}</div>
                      <div className="font-mono font-bold text-white text-xs">→ {formatCurrency(statement.endingValue)}</div>
                    </td>
                    <td className={cn(
                      "py-4 px-4 text-right font-bold",
                      statement.periodReturn >= 0 ? "text-emerald-400" : "text-red-400"
                    )}>
                      {statement.periodReturn >= 0 ? '+' : ''}{statement.periodReturn.toFixed(2)}%
                    </td>
                    <td className="py-4 px-4 text-center">
                      {getStatusBadge(statement.status)}
                    </td>
                    <td className="py-4 px-4 text-center text-slate-400">
                      {statement.viewCount > 0 ? (
                        <span className="flex items-center justify-center gap-1">
                          <Eye className="w-3 h-3" />
                          {statement.viewCount}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePreview(statement)}
                          className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(statement)}
                          className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {statement.status === 'PENDING_APPROVAL' && (
                          <button
                            onClick={() => handleApprove(statement.id)}
                            className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all"
                            title="Approve"
                          >
                            <FileCheck className="w-4 h-4" />
                          </button>
                        )}
                        {statement.status === 'APPROVED' && (
                          <button
                            onClick={() => handleSend(statement.id)}
                            className="p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-all"
                            title="Send to Client"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredStatements.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-slate-500 italic">
                      No statements found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* Batch Actions */}
        {pendingCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between p-4 bg-gradient-to-r from-violet-600/20 to-blue-600/20 border border-violet-500/30 rounded-2xl"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-white">Batch Send Available</div>
                <div className="text-xs text-slate-400">{pendingCount} statements ready to be sent to clients</div>
              </div>
            </div>
            <div className="flex gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold rounded-xl transition-all border border-white/10">
                <Printer className="w-4 h-4" />
                Print All
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold rounded-xl transition-all">
                <Send className="w-4 h-4" />
                Send All ({pendingCount})
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </Container>
  );
};

export default ClientStatementsScreen;
